/**
 * /admin/email-logs — Delivery health & audit for transactional email.
 *
 * - Live stats: 24h/7d/30d success rate + provider status
 * - Top templates in the last 30 days
 * - Filterable log table (status / template / recipient substring)
 * - Manual "resend" for any single row (re-renders the original template)
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { adminOpsAPI } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Mail, RefreshCw, Loader2, ShieldAlert, CheckCircle2, XCircle,
  AlertCircle, Send, Activity, Clock, Zap,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_FILTERS = [
  { value: 'all', label: 'All statuses' },
  { value: 'sent', label: 'Sent' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'opened', label: 'Opened' },
  { value: 'clicked', label: 'Clicked' },
  { value: 'bounced', label: 'Bounced' },
  { value: 'complained', label: 'Complained' },
  { value: 'delayed', label: 'Delayed' },
  { value: 'failed', label: 'Failed' },
  { value: 'simulated', label: 'Simulated' },
];

const STATUS_STYLE = {
  sent: 'bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30',
  delivered: 'bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30',
  opened: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30',
  clicked: 'bg-teal-500/15 text-teal-700 dark:text-teal-400 border-teal-500/30',
  bounced: 'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30',
  complained: 'bg-orange-500/15 text-orange-700 dark:text-orange-400 border-orange-500/30',
  delayed: 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-500/30',
  failed: 'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30',
  simulated: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30',
};

const StatusIcon = ({ status }) => {
  if (['sent', 'delivered', 'opened', 'clicked'].includes(status)) return <CheckCircle2 className="w-3.5 h-3.5" />;
  if (['bounced', 'complained', 'failed'].includes(status)) return <XCircle className="w-3.5 h-3.5" />;
  return <AlertCircle className="w-3.5 h-3.5" />;
};

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

function StatCard({ label, value, sub, tone = 'default', testid, icon: Icon }) {
  const toneClass = {
    default: 'text-foreground',
    good: 'text-green-700 dark:text-green-400',
    bad: 'text-red-700 dark:text-red-400',
    warn: 'text-amber-700 dark:text-amber-400',
  }[tone];
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="text-xs text-muted-foreground uppercase tracking-wider">{label}</span>
        {Icon ? <Icon className="w-4 h-4 text-muted-foreground" /> : null}
      </div>
      <div className={`text-3xl font-bold tracking-tight ${toneClass}`}>{value}</div>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </Card>
  );
}

export default function AdminEmailLogs() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingRows, setLoadingRows] = useState(true);
  const [resendingId, setResendingId] = useState(null);
  const [filters, setFilters] = useState({ status: 'all', template: 'all', recipient: '' });
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const r = await adminOpsAPI.emailStats();
      setStats(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load email stats');
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const loadRows = useCallback(async () => {
    setLoadingRows(true);
    try {
      const params = { limit: 200 };
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.template !== 'all') params.template = filters.template;
      if (filters.recipient.trim()) params.recipient = filters.recipient.trim();
      const r = await adminOpsAPI.emailLogs(params);
      setRows(r.data?.rows || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load email logs');
    } finally {
      setLoadingRows(false);
    }
  }, [filters]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const handleResend = async (logId) => {
    if (!logId) {
      toast.error('This log entry cannot be resent (no id).');
      return;
    }
    setResendingId(logId);
    try {
      const r = await adminOpsAPI.emailResend(logId);
      if (r.data?.resent) {
        toast.success(`Resent to ${r.data.to}`);
      } else if (r.data?.mode === 'simulated') {
        toast.info('Simulated — no RESEND_API_KEY configured.');
      } else {
        toast.error(r.data?.error || 'Resend failed');
      }
      await Promise.all([loadStats(), loadRows()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Resend failed');
    } finally {
      setResendingId(null);
    }
  };

  const handleSendTest = async () => {
    const email = testEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Enter a valid email address.');
      return;
    }
    setSendingTest(true);
    try {
      const r = await adminOpsAPI.sendTestEmail(email);
      if (r.data?.status === 'sent') {
        toast.success(`Test email sent to ${email} (id: ${r.data.message_id?.slice(0, 8) || 'n/a'})`);
      } else if (r.data?.status === 'simulated') {
        toast.info(r.data?.note || 'Simulated — no RESEND_API_KEY configured.');
      } else {
        toast.error(r.data?.error || 'Send failed');
      }
      setTestDialogOpen(false);
      setTestEmail('');
      await Promise.all([loadStats(), loadRows()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Send failed');
    } finally {
      setSendingTest(false);
    }
  };

  if (user && user.role !== 'admin') {
    return (
      <div className="max-w-xl mx-auto py-20 text-center" data-testid="admin-email-denied">
        <ShieldAlert className="w-12 h-12 mx-auto text-destructive mb-3" />
        <h1 className="text-xl font-bold mb-2">Admin only</h1>
        <p className="text-muted-foreground mb-4">
          Email delivery logs are restricted to platform administrators.
        </p>
        <Button onClick={() => navigate('/')} data-testid="admin-email-back-home">
          Back to dashboard
        </Button>
      </div>
    );
  }

  const w24 = stats?.windows?.['24h'];
  const w7 = stats?.windows?.['7d'];
  const w30 = stats?.windows?.['30d'];
  const provider = stats?.provider || 'log';
  const templates = stats?.templates || [];

  return (
    <div className="space-y-6 max-w-6xl" data-testid="admin-email-logs-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight flex items-center gap-2">
            <Mail className="w-6 h-6 text-primary" /> Email Delivery Logs
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every transactional email flowing through <code className="text-xs bg-muted px-1 rounded">services/email_service.py</code> is
            logged here — success rates, per-template health, and one-click resend for anything that failed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className={provider === 'resend' ? 'border-green-500/40 text-green-700 dark:text-green-400' : 'border-amber-500/40 text-amber-700 dark:text-amber-400'}
            data-testid="email-provider-badge"
          >
            Provider: {provider === 'resend' ? 'Resend (live)' : 'Simulated (no key)'}
          </Badge>
          <Button size="sm" onClick={() => setTestDialogOpen(true)} data-testid="send-test-email-btn">
            <Zap className="w-4 h-4 mr-2" /> Send Test Email
          </Button>
          <Button variant="outline" size="sm" onClick={() => { loadStats(); loadRows(); }} disabled={loadingStats || loadingRows} data-testid="refresh-email-logs-btn">
            {(loadingStats || loadingRows) ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="email-stats-cards">
        <StatCard
          label="Success rate (24h)"
          value={loadingStats ? '…' : `${w24?.success_rate ?? 100}%`}
          sub={`${w24?.total ?? 0} attempts`}
          tone={(w24?.success_rate ?? 100) >= 95 ? 'good' : (w24?.success_rate ?? 100) >= 80 ? 'warn' : 'bad'}
          icon={Activity}
          testid="stat-24h-rate"
        />
        <StatCard
          label="Sent (24h)"
          value={loadingStats ? '…' : (w24?.sent ?? 0)}
          sub={`${w24?.simulated ?? 0} simulated`}
          tone="good"
          icon={Send}
          testid="stat-24h-sent"
        />
        <StatCard
          label="Failed (24h)"
          value={loadingStats ? '…' : (w24?.failed ?? 0)}
          sub="click Resend below to retry"
          tone={(w24?.failed ?? 0) > 0 ? 'bad' : 'default'}
          icon={XCircle}
          testid="stat-24h-failed"
        />
        <StatCard
          label="7-day volume"
          value={loadingStats ? '…' : (w7?.total ?? 0)}
          sub={`${w30?.total ?? 0} in 30 days`}
          icon={Clock}
          testid="stat-7d-total"
        />
      </div>

      {/* Templates breakdown */}
      <Card className="p-0 overflow-hidden" data-testid="templates-breakdown-card">
        <div className="p-5 border-b border-border">
          <h2 className="font-semibold">Top templates · last 30 days</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Highest-volume senders — spot noise or a template with an unusual failure rate.</p>
        </div>
        <div className="cp-table-wrapper">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Template</th>
                <th className="px-4 py-3 font-medium text-right">Total</th>
                <th className="px-4 py-3 font-medium text-right">Sent</th>
                <th className="px-4 py-3 font-medium text-right">Failed</th>
                <th className="px-4 py-3 font-medium text-right">Simulated</th>
                <th className="px-4 py-3 font-medium text-right">Success</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loadingStats ? (
                <tr><td colSpan="6" className="px-4 py-10 text-center text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin inline" />
                </td></tr>
              ) : templates.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-10 text-center text-muted-foreground" data-testid="no-templates-row">
                  No transactional emails in the last 30 days yet.
                </td></tr>
              ) : templates.map((t) => {
                const rate = t.total ? Math.round(((t.sent + (t.simulated || 0)) / t.total) * 100) : 100;
                return (
                  <tr key={t.template} className="hover:bg-muted/30" data-testid={`tpl-row-${t.template}`}>
                    <td className="px-4 py-3 font-mono text-xs">{t.template}</td>
                    <td className="px-4 py-3 text-right font-semibold">{t.total}</td>
                    <td className="px-4 py-3 text-right text-green-700 dark:text-green-400">{t.sent}</td>
                    <td className="px-4 py-3 text-right text-red-700 dark:text-red-400">{t.failed || 0}</td>
                    <td className="px-4 py-3 text-right text-amber-700 dark:text-amber-400">{t.simulated || 0}</td>
                    <td className="px-4 py-3 text-right">
                      <Badge variant="outline" className={rate >= 95 ? 'border-green-500/40 text-green-700 dark:text-green-400' : rate >= 80 ? 'border-amber-500/40 text-amber-700 dark:text-amber-400' : 'border-red-500/40 text-red-700 dark:text-red-400'}>
                        {rate}%
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Filters */}
      <Card className="p-4" data-testid="email-filters-card">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Input
              value={filters.recipient}
              onChange={(e) => setFilters((f) => ({ ...f, recipient: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') loadRows(); }}
              placeholder="Filter by recipient (substring)…"
              data-testid="email-filter-recipient"
            />
          </div>
          <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
            <SelectTrigger className="w-full sm:w-48" data-testid="email-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.template} onValueChange={(v) => setFilters((f) => ({ ...f, template: v }))}>
            <SelectTrigger className="w-full sm:w-56" data-testid="email-filter-template"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All templates</SelectItem>
              {templates.map((t) => (
                <SelectItem key={t.template} value={t.template}>{t.template}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Logs table */}
      <Card className="p-0 overflow-hidden" data-testid="email-logs-table-card">
        <div className="cp-table-wrapper">
          <table className="w-full text-sm min-w-[820px]">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">When</th>
                <th className="px-4 py-3 font-medium">Recipient</th>
                <th className="px-4 py-3 font-medium">Subject</th>
                <th className="px-4 py-3 font-medium">Template</th>
                <th className="px-4 py-3 font-medium text-center">Status</th>
                <th className="px-4 py-3 font-medium text-center">Attempts</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loadingRows ? (
                <tr><td colSpan="7" className="px-4 py-10 text-center text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin inline" />
                </td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-10 text-center text-muted-foreground" data-testid="no-email-logs">
                  No email logs match your filters.
                </td></tr>
              ) : rows.map((r) => (
                <tr key={r.provider_message_id || `${r.recipient}-${r.created_at}`} className="hover:bg-muted/30" data-testid={`email-row-${r.provider_message_id || r.created_at}`}>
                  <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{fmt(r.created_at)}</td>
                  <td className="px-4 py-3 text-xs">{r.recipient}</td>
                  <td className="px-4 py-3 text-xs truncate max-w-xs" title={r.subject}>{r.subject || '—'}</td>
                  <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">{r.template || '(inline)'}</td>
                  <td className="px-4 py-3 text-center">
                    <Badge variant="outline" className={`gap-1 ${STATUS_STYLE[r.status] || ''}`}>
                      <StatusIcon status={r.status} /> {r.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-center font-mono">{r.attempts ?? 1}</td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleResend(r.id || r._id)}
                      disabled={resendingId === (r.id || r._id) || (!r.id && !r._id)}
                      data-testid={`resend-btn-${r.provider_message_id || r.created_at}`}
                    >
                      {resendingId === (r.id || r._id)
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <Send className="w-3.5 h-3.5" />}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="text-xs text-muted-foreground">
        Retention: <strong>unbounded</strong> — email_logs is a compliance-grade audit trail. Prune manually via
        <code className="text-[11px] bg-muted px-1 rounded ml-1">db.email_logs.deleteMany</code> if size grows large.
      </p>

      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent data-testid="send-test-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" /> Send test email
            </DialogTitle>
            <DialogDescription>
              Delivers a small verification email through the currently-configured provider.
              When <code className="text-xs bg-muted px-1 rounded">RESEND_API_KEY</code> is missing, the message is only
              logged (mode: simulated) so you can still verify the wiring end-to-end.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label htmlFor="test-email-input" className="text-sm font-medium">Recipient</label>
            <Input
              id="test-email-input"
              type="email"
              placeholder="you@example.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !sendingTest) handleSendTest(); }}
              data-testid="test-email-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestDialogOpen(false)} data-testid="test-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleSendTest} disabled={sendingTest} data-testid="test-send-btn">
              {sendingTest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
